import { useState } from "react";
import {
  EscrowConsent, Field, FileField, GeoField, ID_TYPES, Input, NG_STATES, OnboardShell, Select, useKycSubmit,
} from "@/pages/onboarding/shared";
import { useAuth } from "@/context/AuthContext";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { LocationPin } from "@/components/LocationPin";

const STEPS = ["Personal", "Government ID", "Selfie", "Address & consent"];

export default function MasterClientKyc() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const { busy, submit } = useKycSubmit("/api/kyc/master-client");
  const [f, setF] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    id_type: "",
    id_number: "",
    id_document_file_id: "",
    selfie_file_id: "",
    physical_address: "",
    city: "",
    state: "",
    geolocation: null,
    emergency_contact_name: "",
    emergency_contact_phone: "",
    escrow_terms_accepted: false,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const canNext = [
    f.full_name && f.phone && f.email,
    f.id_type && f.id_number && f.id_document_file_id,
    !!f.selfie_file_id,
    f.physical_address && f.city && f.state && f.geolocation && f.escrow_terms_accepted,
  ][step];

  const next = () => (step === STEPS.length - 1 ? submit(f) : setStep(step + 1));

  return (
    <OnboardShell
      title="Master Client verification"
      subtitle="Care happens in your home, so we verify every client. Documents are stored privately."
      steps={STEPS}
      step={step}
      onBack={() => setStep(step - 1)}
      onNext={next}
      busy={busy}
      canNext={canNext}
      submitLabel="Submit for review"
    >
      {step === 0 && (
        <>
          <Field label="Full name"><Input value={f.full_name} onChange={(e) => set("full_name")(e.target.value)} data-testid="mc-fullname-input" /></Field>
          <Field label="Phone number"><Input value={f.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="mc-phone-input" /></Field>
          <Field label="Email address"><Input type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="mc-email-input" /></Field>
          <Field label="Emergency contact name"><Input value={f.emergency_contact_name} onChange={(e) => set("emergency_contact_name")(e.target.value)} data-testid="mc-emergency-name-input" /></Field>
          <Field label="Emergency contact phone"><Input value={f.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone")(e.target.value)} data-testid="mc-emergency-phone-input" /></Field>
        </>
      )}
      {step === 1 && (
        <>
          <Field label="Government ID type"><Select value={f.id_type} onChange={set("id_type")} options={ID_TYPES} testId="mc-idtype-select" /></Field>
          <Field label="ID number"><Input value={f.id_number} onChange={(e) => set("id_number")(e.target.value)} data-testid="mc-idnumber-input" /></Field>
          <FileField label="ID document photo" value={f.id_document_file_id} onChange={set("id_document_file_id")} purpose="GOVERNMENT_ID" testId="mc-iddoc-upload" />
        </>
      )}
      {step === 2 && (
        <>
          <p className="text-sm text-slate-600">
            Upload a clear selfie. It is matched against your government ID by our verification team.
          </p>
          <FileField label="Biometric selfie" value={f.selfie_file_id} onChange={set("selfie_file_id")} purpose="BIOMETRIC_SELFIE" testId="mc-selfie-upload" />
        </>
      )}
      {step === 3 && (
        <>
          <Field label="Physical address">
            <AddressAutocomplete
              value={f.physical_address}
              onChange={set("physical_address")}
              onResolved={(p) => setF((prev) => ({ ...prev, physical_address: p.address || prev.physical_address, geolocation: p.geolocation || prev.geolocation }))}
              placeholder="Start typing your address…"
              testId="mc-address-input"
            />
          </Field>
          <LocationPin geolocation={f.geolocation} address={f.physical_address} testId="mc-address-pin" />
          <Field label="City / town"><Input value={f.city} onChange={(e) => set("city")(e.target.value)} data-testid="mc-city-input" /></Field>
          <Field label="State"><Select value={f.state} onChange={set("state")} options={NG_STATES} testId="mc-state-select" /></Field>
          <GeoField value={f.geolocation} onChange={set("geolocation")} testId="mc-geo-btn" />
          <EscrowConsent checked={f.escrow_terms_accepted} onChange={set("escrow_terms_accepted")} testId="mc-escrow-checkbox" />
        </>
      )}
    </OnboardShell>
  );
}
